/**
 * BootstrapGate (TMMAPP-0003). Wraps the router: nothing renders until `GET /api/bootstrap` has
 * resolved. Pending is a loading state; failure is a BLOCKING screen with a Retry
 * button — never a degraded shell running on a guessed or composed host (MM-D-28). Both render
 * with the cached or built-in brand theme (TMMAPP-0035), never unstyled. Once the bootstrap names
 * the auth origin, the brand theme is brought up to date before the children render: at once
 * from a cached copy (revalidated in the background), else after at most 1,500 ms.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBrandTheme } from '@/context/brand-theme-context';
import { HiddenPageTitle } from '@/shell/PageHeader';
import { StandaloneFrame } from '@/shell/StandaloneFrame';
import { StandaloneMain } from '@/shell/StandaloneMain';
import { brandDiagnosticsEnabled, syncBrandTheme } from '@/theme/brandTheme';
import { BootstrapError, resolveBootstrap } from './bootstrap';
import { env } from './env';

type GateState =
  { status: 'pending' } | { status: 'ready' } | { status: 'failed'; error: BootstrapError };

function toBootstrapError(error: unknown): BootstrapError {
  if (error instanceof BootstrapError) return error;
  return new BootstrapError(error instanceof Error ? error.message : String(error), null);
}

function isNotRegistered(error: BootstrapError): boolean {
  return error.status === 404 || error.code === 'HOSTNAME_NOT_REGISTERED';
}

export function BootstrapGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { setBrandTheme } = useBrandTheme();
  const [state, setState] = useState<GateState>({ status: 'pending' });
  // StrictMode double-invokes the mount effect; the bootstrap request is issued once.
  const started = useRef(false);

  const load = useCallback(async () => {
    setState({ status: 'pending' });
    try {
      const bootstrap = await resolveBootstrap();
      // The theme is never critical: it can never fail the bootstrap.
      // Why a brand theme was rejected or failed is logged everywhere but production
      // (TMMAPP-0037); either the build flag or the bootstrap saying production silences it.
      const diagnostics = brandDiagnosticsEnabled({
        dev: import.meta.env.DEV,
        buildEnv: env.deployEnv,
        bootstrapEnv: bootstrap.deployEnv,
      });
      await syncBrandTheme(bootstrap.authOrigin, setBrandTheme, { diagnostics }).catch(
        () => undefined,
      );
      setState({ status: 'ready' });
    } catch (error) {
      setState({ status: 'failed', error: toBootstrapError(error) });
    }
  }, [setBrandTheme]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  if (state.status === 'ready') return <>{children}</>;

  if (state.status === 'failed') {
    const notRegistered = isNotRegistered(state.error);
    return (
      <StandaloneFrame>
        {/* The alert sits inside the landmark: a `role="alert"` on the `main` itself would replace it. */}
        <Box role="alert">
          <Typography variant="h1" gutterBottom>
            {t(notRegistered ? 'bootstrap.notRegisteredTitle' : 'bootstrap.failedTitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t(notRegistered ? 'bootstrap.notRegisteredBody' : 'bootstrap.failedBody')}
          </Typography>
          <Button variant="contained" onClick={() => void load()}>
            {t('common.retry')}
          </Button>
        </Box>
      </StandaloneFrame>
    );
  }

  return (
    <StandaloneMain sx={{ py: 6 }}>
      <HiddenPageTitle title={t('common.loading')} />
      <Typography variant="body1" role="status">
        {t('common.loading')}
      </Typography>
    </StandaloneMain>
  );
}
